import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  approveAdminListing,
  createAdminListing,
  rejectAdminListing,
  updateAdminListing,
} from '@/services/api/adminListingsApi';

const invalidateAdminListingQueries = async (
  queryClient: ReturnType<typeof useQueryClient>,
  listingId?: string,
) => {
  await queryClient.invalidateQueries({
    predicate: (query) => {
      const rootKey = query.queryKey[0];
      return rootKey === 'admin-listings-queue'
        || rootKey === 'admin-edit-listing'
        || rootKey === 'admin-listing-review-payload'
        || rootKey === 'allProviders'
        || rootKey === 'serviceProvider';
    },
  });

  if (listingId) {
    await queryClient.invalidateQueries({ queryKey: ['admin-edit-listing', listingId] });
    await queryClient.invalidateQueries({ queryKey: ['serviceProvider', listingId] });
  }
};

export const useCreateAdminListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAdminListing,
    onSuccess: async () => {
      await invalidateAdminListingQueries(queryClient);
    },
  });
};

export const useUpdateAdminListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAdminListing,
    onSuccess: async (result) => {
      await invalidateAdminListingQueries(queryClient, result.listingId);
    },
  });
};

export const useApproveAdminListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: approveAdminListing,
    onSuccess: async (result) => {
      await invalidateAdminListingQueries(queryClient, result.listingId);
    },
  });
};

export const useRejectAdminListing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectAdminListing,
    onSuccess: async (result) => {
      await invalidateAdminListingQueries(queryClient, result.listingId);
    },
  });
};